import { create } from 'zustand';
import { message } from 'antd';
import axiosInstance from '../utils/axiosInstance';
import { getCachedData, setCachedData, clearCacheByKey } from '../utils/cacheUtil';

const formatTodoData = (todoData) => {
  const formatted = { ...todoData };
  // Convert DatePicker moment object to ISO string if it exists
  if (formatted.due_date) {
    formatted.due_date = formatted.due_date.toISOString();
  }
  return formatted;
};

const useTodoStore = create((set, get) => ({
  todos: [],
  loading: false,
  error: null,

  // Helper function for optimistic updates
  updateState: (updater) => {
    set(updater);
  },

  fetchTodos: async () => {
    const cacheKey = 'todos';
    const cached = getCachedData(cacheKey);
    if (cached) {
      set({ todos: cached, loading: false, error: null });
      return;
    }

    set({ loading: true });
    try {
      const response = await axiosInstance.get('/todos/');
      setCachedData(cacheKey, response.data);
      set({ todos: response.data, loading: false, error: null });
    } catch (error) {
      set({ error: error.message, loading: false });
      message.error('Failed to fetch todos');
    }
  },

  createTodo: async (todoData) => {
    // Create temporary ID for optimistic update
    const tempId = `temp-${Date.now()}`;
    const newTodo = { ...todoData, id: tempId };
    
    // Optimistic update
    set(state => ({
      todos: [...state.todos, newTodo],
    }));

    try {
      const formattedData = formatTodoData(todoData);
      const response = await axiosInstance.post('/todos/', formattedData);
      clearCacheByKey('todos');
      // Replace temporary todo with real one
      set(state => ({
        todos: state.todos.map(todo => 
          todo.id === tempId ? response.data : todo
        ),
      }));
      message.success('Todo created successfully');
      return { success: true, data: response.data };
    } catch (error) {
      // Rollback on failure
      set(state => ({
        todos: state.todos.filter(todo => todo.id !== tempId),
      }));
      message.error('Failed to create todo');
      return { success: false, error };
    }
  },

  updateTodo: async (id, todoData) => {
    const originalTodo = get().todos.find(t => t.id === id);
    const isAssigneeUpdate = 'assignee' in todoData && Object.keys(todoData).length === 1;
    
    // Optimistic update
    set(state => ({
      todos: state.todos.map(todo => 
        todo.id === id ? { ...todo, ...todoData } : todo
      ),
    }));

    try {
      // For assignee updates, we need to send only specific fields to avoid validation errors
      const dataToSend = isAssigneeUpdate ? {
        title: originalTodo.title,
        description: originalTodo.description,
        completed: originalTodo.completed,
        assignee: todoData.assignee,
        due_date: originalTodo.due_date
      } : formatTodoData(todoData);

      const response = await axiosInstance.put(`/todos/${id}/`, dataToSend);
      clearCacheByKey('todos');
      
      // Update with server response
      set(state => ({
        todos: state.todos.map(todo => 
          todo.id === id ? response.data : todo
        ),
      }));
      
      message.success('Todo updated successfully');
      return { success: true, data: response.data };
    } catch (error) {
      // Rollback on failure
      set(state => ({
        todos: state.todos.map(todo => 
          todo.id === id ? originalTodo : todo
        ),
      }));
      message.error('Failed to update todo');
      return { success: false, error };
    }
  },

  deleteTodo: async (id) => {
    // Store deleted todo for potential rollback
    const deletedTodo = get().todos.find(t => t.id === id);
    
    // Optimistic update
    set(state => ({
      todos: state.todos.filter(todo => todo.id !== id),
    }));

    try {
      await axiosInstance.delete(`/todos/${id}/`);
      clearCacheByKey('todos');
      message.success('Todo deleted successfully');
      return { success: true };
    } catch (error) {
      // Rollback on failure
      set(state => ({
        todos: [...state.todos, deletedTodo],
      }));
      message.error('Failed to delete todo');
      return { success: false, error };
    }
  },

  toggleTodoStatus: async (id) => {
    const todo = get().todos.find(t => t.id === id);
    if (!todo) return;

    // Optimistic update
    set((state) => ({
      todos: state.todos.map((t) =>
        t.id === id ? { ...t, completed: !t.completed } : t
      ),
    }));

    try {
      const response = await axiosInstance.put(`/todos/${id}/`, {
        ...todo,
        completed: !todo.completed
      });
      clearCacheByKey('todos');
      return { success: true, data: response.data };
    } catch (error) {
      // Revert on failure
      set((state) => ({
        todos: state.todos.map((t) =>
          t.id === id ? { ...t, completed: todo.completed } : t
        ),
      }));
      message.error('Failed to update todo status');
      return { success: false, error };
    }
  }
}));

export default useTodoStore;
